import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import { acceptClientInvite, getInvite } from "../../js/api/clientInvite";
import { writeClientPortal } from "../../js/hooks/useClientPortal";
import { useAuth } from "../../js/hooks/useAuth";

export default function ClientSetup() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const invite = getInvite(token);
  const [form, setForm] = useState({
    name: invite?.name || "",
    phone: invite?.phone || "",
    password: "",
  });
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  function update(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  async function submit(event) {
    event.preventDefault();
    setError("");
    try {
      const result = await acceptClientInvite(token, form);
      writeClientPortal(result.client);
      try {
        await login({ email: result.client.email, password: form.password }, result.client.role_type);
      } catch {
        /* portal access still follows the Client ID */
      }
      setDone(result);
    } catch (err) {
      setError(err.message || "Setup did not finish.");
    }
  }

  if (!invite) {
    return (
      <AppLayout variant="public">
        <article className="card account-card">
          <h1>Invitation not found</h1>
          <p>Ask your advisor to send a current REChecklist invitation.</p>
          <Link to="/">Return home</Link>
        </article>
      </AppLayout>
    );
  }

  if (invite.status === "accepted" && !done) {
    return (
      <AppLayout variant="public">
        <article className="card account-card">
          <h1>This setup is complete</h1>
          <p>Your Client ID is {invite.clientCode || "already on file"}. Sign in or use Client Access in the menu.</p>
          <Link to="/login">Sign in</Link>
        </article>
      </AppLayout>
    );
  }

  if (done) {
    return (
      <AppLayout variant="client">
        <article className="card account-card">
          <p className="home-kicker">{done.client.role_type === "seller" ? "Seller" : "Buyer"} access</p>
          <h1>You are connected</h1>
          <p className="page-lead">
            Your Client ID is <strong>{done.client.client_code}</strong>. This is how you and{" "}
            {invite.advisorName || "your advisor"} stay connected.
          </p>
          <Link className="primary-btn" to={done.portalPath}>
            Open my portal
          </Link>
        </article>
      </AppLayout>
    );
  }

  const kind = invite.kind === "seller" ? "seller" : "buyer";

  return (
    <AppLayout variant="public">
      <article className="card account-card start-here__form">
        <p className="home-kicker">Account setup</p>
        <h1>Complete your REChecklist {kind} setup</h1>
        <p className="page-lead">
          {invite.advisorName || "Your advisor"} invited you. Finish this form to receive your Client ID
          and open only your {kind} portal.
        </p>
        <form onSubmit={submit}>
          <label className="field">
            <span className="field-label">Name</span>
            <input className="field-input" name="name" value={form.name} onChange={update} required />
          </label>
          <label className="field">
            <span className="field-label">Email</span>
            <input className="field-input" value={invite.email} readOnly />
          </label>
          <label className="field">
            <span className="field-label">Phone (optional)</span>
            <input className="field-input" name="phone" value={form.phone} onChange={update} />
          </label>
          <label className="field">
            <span className="field-label">Create a password</span>
            <input
              className="field-input"
              name="password"
              type="password"
              value={form.password}
              onChange={update}
              autoComplete="new-password"
              required
              minLength={6}
            />
          </label>
          {error ? <p className="field-error">{error}</p> : null}
          <div className="btn-row">
            <button type="submit" className="primary-btn">
              Finish setup
            </button>
            <button type="button" className="ghost-btn" onClick={() => navigate("/")}>
              Cancel
            </button>
          </div>
        </form>
      </article>
    </AppLayout>
  );
}
